/*
 * Operator-realm REST client. The operator console authenticates with the OPERATOR
 * JWT (api OPERATOR_JWT_SECRET), which is cryptographically distinct from the admin
 * JWT — so it gets its OWN sessionStorage token key + its OWN 401 handler, wired
 * through apiFetch's per-request `token` + `onAuthError` overrides. The admin realm
 * (api/client.ts token store + global handler) is never touched by operator calls.
 */
import type { ZodType } from "zod";
import { apiFetch, type ApiRequestOptions } from "../client";

// Distinct from the admin "cogent.token" so the two sessions never collide.
const OP_TOKEN_KEY = "cogent.operator.token";

export function getOperatorToken(): string | null {
  try {
    return sessionStorage.getItem(OP_TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setOperatorToken(token: string): void {
  try {
    sessionStorage.setItem(OP_TOKEN_KEY, token);
  } catch {
    /* storage disabled — auth won't persist across reloads */
  }
}
export function clearOperatorToken(): void {
  try {
    sessionStorage.removeItem(OP_TOKEN_KEY);
  } catch {
    /* no-op */
  }
}

// The OperatorAuthContext registers a handler so an expired operator token fires a
// single operator-logout (separate from the admin global handler).
let onOperatorUnauthorized: (() => void) | null = null;
export function setOperatorUnauthorizedHandler(fn: (() => void) | null): void {
  onOperatorUnauthorized = fn;
}

/** An authenticated operator request — sends the operator bearer + realm-specific 401 logout. */
export function operatorFetch<T>(
  path: string,
  opts: Omit<ApiRequestOptions, "auth" | "token" | "onAuthError"> & { schema?: ZodType<T> } = {},
): Promise<T> {
  return apiFetch<T>(path, {
    ...opts,
    auth: true,
    token: getOperatorToken(),
    onAuthError: () => {
      clearOperatorToken();
      onOperatorUnauthorized?.();
    },
  });
}
